import { useState, useEffect, useMemo, useCallback } from "react";
import { doc, getDoc, updateDoc, collection, query, where, getDocs, limit } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuthContext } from "../../contexts/AuthContext";
import { useSubscription } from "../../hooks/useSubscription";
import Card from "../../components/Card";
import { useNavigate } from "react-router-dom";
import MediaUpload from "../../components/MediaUpload";
import SubdomainDashboard from "./SubdomainDashboard";
import ThemeSelector, { ThemeLayout } from "../../components/ThemeSelector";
import RichTextEditor from "../../components/RichTextEditor";
import { Settings, User, Palette, Link as LinkIcon, CreditCard, Save, CheckCircle, Gem, Smartphone, Globe, Share2, Loader, X, Check, AlertCircle, ExternalLink, BookOpen } from "lucide-react";
import { createCustomDomainRequest } from "../../services/customDomainService";

export default function InstructorSettings() {
  const { user } = useAuthContext();
  const { plan } = useSubscription();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [bio, setBio] = useState("");
  const [paymentQRCode, setPaymentQRCode] = useState("");
  const [bankAccount, setBankAccount] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountName, setAccountName] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [profileImageUploaded, setProfileImageUploaded] = useState(false);
  const [profileImageKey, setProfileImageKey] = useState(Date.now());
  const [customDomain, setCustomDomain] = useState("");
  const [useSubdomain, setUseSubdomain] = useState(true);
  const [coverImage, setCoverImage] = useState("");
  const [coverImageUploaded, setCoverImageUploaded] = useState(false);
  const [coverImageKey, setCoverImageKey] = useState(Date.now());
  const [introText, setIntroText] = useState("");
  const [introImage, setIntroImage] = useState("");
  const [introImageUploaded, setIntroImageUploaded] = useState(false);
  const [introImageKey, setIntroImageKey] = useState(Date.now());
  const [paymentQRUploaded, setPaymentQRUploaded] = useState(false);
  const [socialLinks, setSocialLinks] = useState({
    facebook: "",
    line: "",
    instagram: "",
    youtube: "",
    website: ""
  });
  const [themeColor, setThemeColor] = useState("#8B5CF6");
  const [headingColor, setHeadingColor] = useState("#1F2937");
  const [buttonColor, setButtonColor] = useState("#8B5CF6");
  const [themeLayout, setThemeLayout] = useState<ThemeLayout>("classic");
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState<"profile" | "appearance" | "url" | "payment">("profile");
  
  // Username validation states
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [usernameErrorMessage, setUsernameErrorMessage] = useState<string | null>(null);
  const [originalUsername, setOriginalUsername] = useState("");
  const [existingUsername, setExistingUsername] = useState<string | null>(null);
  const [useExistingUsername, setUseExistingUsername] = useState(true);

  // URL addon states
  const [hasUrlAddon, setHasUrlAddon] = useState(false);
  const [urlAddonType, setUrlAddonType] = useState<'path' | 'subdomain' | null>(null);

  const tier = (user as any)?.subscriptionTier || "free";

  // Stable callback references
  const handleProfileUploadComplete = useCallback((mediaId: string, url: string) => {
    console.log('[InstructorSettings] Profile upload complete:', { mediaId, url });
    const newKey = Date.now();
    setProfileImage(url);
    setProfileImageUploaded(true);
    setProfileImageKey(newKey);
    console.log('[InstructorSettings] Profile state updated with key:', newKey);
  }, []);

  const handleCoverUploadComplete = useCallback((mediaId: string, url: string) => {
    console.log('[handleCoverUploadComplete] Called with URL:', url);
    
    if (!url || url.includes('Invalid-Image-URL') || url.includes('placeholder')) {
      alert('เกิดข้อผิดพลาด: URL รูปภาพไม่ถูกต้อง');
      return;
    }
    
    console.log('[handleCoverUploadComplete] Setting coverImage to:', url);
    setCoverImage(url);
    setCoverImageUploaded(true);
    setCoverImageKey(Date.now());
  }, []);

  const handleIntroUploadComplete = useCallback((mediaId: string, url: string) => {
    console.log('[InstructorSettings] Intro upload complete:', { mediaId, url });
    const newKey = Date.now();
    setIntroImage(url);
    setIntroImageUploaded(true);
    setIntroImageKey(newKey);
    console.log('[InstructorSettings] Intro state updated with key:', newKey);
  }, []);

  useEffect(() => {
    async function loadProfile() {
      if (!user) return;

      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setName(data.name || "");
          const loadedUsername = data.username || "";
          setUsername(loadedUsername);
          setOriginalUsername(loadedUsername);
          if (loadedUsername) {
            setExistingUsername(loadedUsername);
            setUseExistingUsername(true);
          }
          setBio(data.bio || "");
          setPaymentQRCode(data.paymentQRCode || "");
          setBankAccount(data.bankAccount || "");
          setBankName(data.bankName || "");
          setAccountName(data.accountName || "");
          setProfileImage(data.profileImage || "");
          setCustomDomain(data.customDomain || "");
          setUseSubdomain(data.useSubdomain !== false);
          const coverImageUrl = data.coverImage || "";
          if (coverImageUrl && !coverImageUrl.includes('Invalid-Image-URL') && !coverImageUrl.includes('placeholder')) {
            setCoverImage(coverImageUrl);
          } else {
            setCoverImage("");
          }
          setIntroText(data.introText || "");
          setIntroImage(data.introImage || "");
          setSocialLinks(data.socialLinks || {
            facebook: "",
            line: "",
            instagram: "",
            youtube: "",
            website: ""
          });
          setThemeColor(data.themeColor || "#8B5CF6");
          setHeadingColor(data.headingColor || "#1F2937");
          setButtonColor(data.buttonColor || "#8B5CF6");
          setThemeLayout(data.themeLayout || "classic");
        }
      } catch (err) {
        console.error("Error loading profile:", err);
      } finally {
        setLoading(false);
      }
    }

    loadProfile();
  }, [user]);

  // Check for URL addon
  useEffect(() => {
    const checkUrlAddon = async () => {
      if (!user?.uid) return;
      
      try {
        const urlAddonDoc = await getDoc(doc(db, 'urlAddons', user.uid));
        if (urlAddonDoc.exists()) {
          const data = urlAddonDoc.data();
          setHasUrlAddon(data.status === 'active');
          setUrlAddonType(data.type);
        }
      } catch (error) {
        console.error('Error checking URL addon:', error);
      }
    };
    
    checkUrlAddon();
  }, [user]);

  // Username validation function
  const validateUsername = async (usernameToCheck: string) => {
    if (!usernameToCheck || usernameToCheck.trim() === '') {
      setUsernameAvailable(null);
      setUsernameErrorMessage(null);
      return;
    }

    // If it's the same as original username, skip validation
    if (usernameToCheck === originalUsername) {
      setUsernameAvailable(true);
      setUsernameErrorMessage(null);
      return;
    }

    setIsCheckingUsername(true);
    setUsernameAvailable(null);
    setUsernameErrorMessage(null);

    const normalizedUsername = usernameToCheck.toLowerCase().trim();

    // Length check
    if (normalizedUsername.length < 3 || normalizedUsername.length > 30) {
      setIsCheckingUsername(false);
      setUsernameAvailable(false);
      setUsernameErrorMessage('ความยาว 3-30 ตัวอักษร');
      return;
    }

    // Thai language check
    const hasThai = /[\u0E00-\u0E7F]/.test(normalizedUsername);
    if (hasThai) {
      setIsCheckingUsername(false);
      setUsernameAvailable(false);
      setUsernameErrorMessage('ไม่สามารถใช้ภาษาไทยได้ กรุณาใช้ภาษาอังกฤษเท่านั้น');
      return;
    }

    // Format check
    const validFormat = /^[a-z0-9_-]+$/.test(normalizedUsername);
    if (!validFormat) {
      setIsCheckingUsername(false);
      setUsernameAvailable(false);
      setUsernameErrorMessage('ใช้ได้เฉพาะ a-z, 0-9, _, - เท่านั้น');
      return;
    }

    // Reserved subdomains check
    const reserved = [
      // Technical
      'www', 'api', 'admin', 'cdn', 'mail', 'ftp', 'smtp', 'salepage', 'edumi',
      // Official/Owner impersonation
      'official', 'office', 'officer', 'headquarters', 'hq', 'main', 'central',
      'staff', 'team', 'support', 'help', 'service', 'customer', 'care',
      'owner', 'founder', 'ceo', 'director', 'manager', 'president',
      // Company/Business
      'info', 'contact', 'about', 'company', 'corporate', 'business', 'enterprise',
      'market', 'marketing', 'sales', 'sale', 'shop', 'store', 'commerce',
      'news', 'blog', 'press', 'media', 'pr', 'public', 'relations',
      // Development
      'dev', 'developer', 'test', 'demo', 'beta', 'alpha', 'staging', 'production',
      // System
      'system', 'root', 'administrator', 'moderator', 'mod', 'super', 'superuser',
      'account', 'billing', 'payment', 'invoice', 'finance', 'accounting',
      // Legal/Security
      'security', 'privacy', 'legal', 'terms', 'policy', 'compliance', 'law',
      // Brand protection
      'brand', 'trademark', 'copyright', 'verified', 'authentic', 'real', 'true'
    ];
    if (reserved.includes(normalizedUsername)) {
      setIsCheckingUsername(false);
      setUsernameAvailable(false);
      setUsernameErrorMessage('ชื่อนี้ถูกสงวนไว้สำหรับระบบ');
      return;
    }

    // Inappropriate words blacklist (profanity, offensive terms)
    const blacklist = [
      // English profanity
      'fuck', 'shit', 'ass', 'bitch', 'dick', 'cock', 'pussy', 'cunt', 'damn', 'hell',
      'bastard', 'whore', 'slut', 'piss', 'nigger', 'nigga', 'fag', 'faggot', 'retard',
      // Thai profanity (romanized)
      'hia', 'ai-hia', 'ai-sat', 'kwai', 'guu', 'mung', 'sathu', 'ai-tua',
      // Common variations and leetspeak
      'fck', 'fuk', 'fuq', 'fvck', 'sht', 'sh1t', 'btch', 'b1tch', 'dck', 'd1ck',
      'psy', 'cnt', 'dmn', 'hll', 'a55',
      'f-u-c-k', 'f_u_c_k', 'f.u.c.k', 'phuck',
      // Offensive terms
      'sex', 'porn', 'xxx', 'nude', 'naked', 'rape', 'anal', 'anus', 'ballsack',
      'blowjob', 'boob', 'butt', 'dildo',
      'kill', 'murder', 'death', 'suicide', 'terrorist', 'bomb',
      'nazi', 'hitler', 'drug', 'cocaine', 'heroin', 'meth',
      // Scam/fraud related
      'scam', 'fraud', 'fake', 'phishing', 'hack', 'hacker', 'crack', 'pirate',
      // Brand impersonation
      'facebook', 'google', 'apple', 'microsoft', 'amazon', 'netflix', 'youtube',
      'instagram', 'twitter', 'tiktok', 'line', 'whatsapp', 'telegram'
    ];

    // Check if username contains any blacklisted word
    const containsBlacklist = blacklist.some(word => normalizedUsername.includes(word));
    if (containsBlacklist) {
      setIsCheckingUsername(false);
      setUsernameAvailable(false);
      setUsernameErrorMessage('ชื่อนี้ไม่เหมาะสมหรือมีคำต้องห้าม');
      return;
    }

    try {
      // Check if subdomain is used by any user (instructor or sale page user)
      const usersQuery = query(
        collection(db, 'users'),
        where('username', '==', normalizedUsername),
        limit(1)
      );
      const usersSnapshot = await getDocs(usersQuery);

      if (!usersSnapshot.empty) {
        setIsCheckingUsername(false);
        setUsernameAvailable(false);
        setUsernameErrorMessage('ชื่อนี้ถูกใช้งานแล้ว');
        return;
      }

      // Check if subdomain is used in a salePage subscription
      const salePageQuery = query(
        collection(db, 'salePageSubscriptions'),
        where('username', '==', normalizedUsername),
        where('hasSubdomain', '==', true),
        limit(1)
      );
      const salePageSnapshot = await getDocs(salePageQuery);

      if (!salePageSnapshot.empty) {
        setIsCheckingUsername(false);
        setUsernameAvailable(false);
        setUsernameErrorMessage('ชื่อนี้ถูกใช้งานแล้ว');
        return;
      }

      // All checks passed - subdomain is available
      setIsCheckingUsername(false);
      setUsernameAvailable(true);
      setUsernameErrorMessage(null);
    } catch (error: any) {
      console.error('Error checking subdomain:', error);
      setIsCheckingUsername(false);
      setUsernameAvailable(false);
      setUsernameErrorMessage('เกิดข้อผิดพลาดในการตรวจสอบ กรุณาลองใหม่');
    }
  };

  // Validate username on change with debounce
  useEffect(() => {
    const isProPlusYearly = tier === 'pro_plus_yearly';
    if (!username || (!hasUrlAddon && !isProPlusYearly)) return;

    const timeoutId = setTimeout(() => {
      validateUsername(username);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [username, hasUrlAddon, tier, originalUsername]);

  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    setError("");
    setMessage("");

    try {
      const updateData: any = {
        name: name.trim(),
        bio: bio.trim(),
        bankAccount: bankAccount.trim(),
        bankName: bankName.trim(),
        accountName: accountName.trim(),
        paymentQRCode: paymentQRCode.trim(),
        profileImage: profileImage.trim(),
        coverImage: coverImage.trim(),
        introText: introText.trim(),
        introImage: introImage.trim(),
        socialLinks: socialLinks,
        themeColor: themeColor,
        headingColor: headingColor,
        buttonColor: buttonColor,
      };

      // Theme layout for Pro tier
      if (tier === "pro" || tier === "pro_plus" || tier === "yearly") {
        updateData.themeLayout = themeLayout;
      }

      console.log('Saving colors:', { themeColor, headingColor, buttonColor });

      // Username only if user has URL add-on or Pro+ Yearly
      const isProPlusYearly = tier === 'pro_plus_yearly';
      if ((hasUrlAddon || isProPlusYearly) && username.trim()) {
        updateData.username = username.trim().toLowerCase();
      }

      // Custom domain or subdomain for Pro
      if (tier === "pro" || tier === "pro_plus" || tier === "yearly") {
        updateData.useSubdomain = useSubdomain;
        if (useSubdomain && username.trim()) {
          updateData.username = username.trim().toLowerCase();
        } else if (!useSubdomain && customDomain.trim()) {
          updateData.customDomain = customDomain.trim().toLowerCase();
        }
      }

      console.log('💾 Saving to Firestore:', updateData);
      console.log('💳 Payment QR Code:', paymentQRCode);
      console.log('🏦 Bank Account:', bankAccount);

      await updateDoc(doc(db, "users", user.uid), updateData);

      setMessage("✅ บันทึกข้อมูลสำเร็จ!");
      
      // Show URL based on tier
      let urlMessage = "";
      if (tier === "free") {
        urlMessage = `\n🔗 URL: ${window.location.origin}/instructor/${user.uid}`;
      } else if ((tier === "pro" || tier === "pro_plus" || tier === "yearly") && username) {
        urlMessage = `\n🔗 URL: ${window.location.origin}/${username}`;
      } else if (tier === "pro" || tier === "pro_plus" || tier === "yearly") {
        if (useSubdomain && username) {
          urlMessage = `\n🔗 URL: ${username}.edumi.co`;
        } else if (!useSubdomain && customDomain) {
          urlMessage = `\n🔗 URL: ${customDomain}`;
        }
      }
      
      setMessage(`✅ บันทึกข้อมูลสำเร็จ!${urlMessage}`);
    } catch (err: any) {
      console.error("Error saving profile:", err);
      setError(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-soft">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-4"></div>
          <p className="text-textLight text-lg">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 py-4 md:py-8 px-3 md:px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header Section */}
        <div className="mb-4 md:mb-6">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <div className="flex items-center gap-2 md:gap-4">
              <div className="w-10 md:w-12 h-10 md:h-12 bg-gradient-to-br from-blue-600 to-pink-600 rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg">
                <Settings className="w-5 md:w-7 h-5 md:h-7 text-white" />
              </div>
              <div>
                <h1 className="text-xl md:text-3xl lg:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-pink-600">
                  ตั้งค่าโปรไฟล์
                </h1>
                <p className="text-gray-600 text-xs md:text-sm hidden md:block">
                  จัดการข้อมูลโปรไฟล์และการรับชำระเงินของคุณ
                </p>
              </div>
            </div>
            <div className={`px-3 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl font-bold text-xs md:text-lg flex items-center gap-1 md:gap-2 shadow-lg ${
              tier === "pro" || tier === "pro_plus" || tier === "yearly"
                ? "bg-gradient-to-r from-blue-600 via-pink-600 to-blue-600 text-white"
                : "bg-gradient-to-r from-gray-400 to-gray-500 text-white"
            }`}>
              <Gem className="w-3 md:w-5 h-3 md:h-5" />
              <span className="hidden sm:inline">{plan?.name || "FREE"}</span>
              <span className="sm:hidden">{tier === "free" ? "FREE" : "PRO"}</span>
            </div>
          </div>
        </div>


        {/* Tabs Navigation */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-1.5 md:p-2 mb-4 md:mb-6 border border-blue-100">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 md:gap-2">
            <button
              onClick={() => setActiveTab("profile")}
              className={`flex items-center justify-center gap-1.5 md:gap-2 px-2 md:px-4 py-2 md:py-3 rounded-lg md:rounded-xl font-semibold text-xs md:text-base transition-all ${
                activeTab === "profile"
                  ? "bg-gradient-to-r from-blue-600 to-pink-600 text-white shadow-lg"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              <User className="w-4 md:w-5 h-4 md:h-5" />
              <span className="hidden sm:inline">ข้อมูลส่วนตัว</span>
              <span className="sm:hidden">โปรไฟล์</span>
            </button>
            <button
              onClick={() => setActiveTab("appearance")}
              className={`flex items-center justify-center gap-1.5 md:gap-2 px-2 md:px-4 py-2 md:py-3 rounded-lg md:rounded-xl font-semibold text-xs md:text-base transition-all ${
                activeTab === "appearance"
                  ? "bg-gradient-to-r from-blue-600 to-pink-600 text-white shadow-lg"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              <Palette className="w-4 md:w-5 h-4 md:h-5" />
              <span>Theme</span>
            </button>
            <button
              onClick={() => setActiveTab("url")}
              className={`flex items-center justify-center gap-1.5 md:gap-2 px-2 md:px-4 py-2 md:py-3 rounded-lg md:rounded-xl font-semibold text-xs md:text-base transition-all ${
                activeTab === "url"
                  ? "bg-gradient-to-r from-blue-600 to-pink-600 text-white shadow-lg"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              <Globe className="w-4 md:w-5 h-4 md:h-5" />
              <span>URL</span>
            </button>
            <button
              onClick={() => setActiveTab("payment")}
              className={`flex items-center justify-center gap-1.5 md:gap-2 px-2 md:px-4 py-2 md:py-3 rounded-lg md:rounded-xl font-semibold text-xs md:text-base transition-all ${
                activeTab === "payment"
                  ? "bg-gradient-to-r from-blue-600 to-pink-600 text-white shadow-lg"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              <CreditCard className="w-4 md:w-5 h-4 md:h-5" />
              <span className="hidden sm:inline">การชำระเงิน</span>
              <span className="sm:hidden">ชำระเงิน</span>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === "profile" && (
        <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl p-4 md:p-6 lg:p-8 mb-4 md:mb-6 border border-blue-100">
          <div className="flex items-center gap-2 md:gap-4 mb-4 md:mb-8">
            <div className="w-10 md:w-12 h-10 md:h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg md:rounded-xl flex items-center justify-center">
              <User className="w-5 md:w-6 h-5 md:h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg md:text-2xl font-bold text-gray-900">
                ข้อมูลส่วนตัว
              </h2>
              <p className="text-xs md:text-sm text-gray-500">ข้อมูลพื้นฐานและรูปภาพของคุณ</p>
            </div>
          </div>

          <div className="space-y-4 md:space-y-6">
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl md:rounded-2xl p-3 md:p-6">
              <label className="block text-xs md:text-base font-bold text-gray-900 mb-1.5 md:mb-2 flex items-center gap-1.5 md:gap-2">
                <User className="w-4 md:w-5 h-4 md:h-5 text-blue-600" />
                รูปโปรไฟล์
              </label>
              <p className="text-[10px] md:text-sm text-gray-600 mb-2 md:mb-4">
                💡 แนะนำ: รูปสี่เหลี่ยมจัตุรัส 500×500px (อัตราส่วน 1:1)
              </p>
              {profileImage ? (
                <div className="flex items-center gap-3 md:gap-4">
                  <img
                    key={`profile-${profileImageKey}`}
                    src={profileImage}
                    alt="Profile Preview"
                    className="w-16 md:w-24 h-16 md:h-24 rounded-full object-cover border-2 md:border-4 border-white shadow-lg"
                    onError={(e) => {
                      console.error('Failed to load profile image:', profileImage);
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  <button
                    onClick={() => {
                      setProfileImage("");
                      setProfileImageUploaded(false);
                      setProfileImageKey(Date.now());
                    }}
                    className="text-xs md:text-sm text-blue-600 hover:text-blue-700 font-semibold"
                  >
                    อัปโหลดรูปใหม่
                  </button>
                </div>
              ) : (
                <MediaUpload
                  key="profile-upload"
                  courseId={user?.uid || "temp"}
                  mediaType="image"
                  onUploadComplete={handleProfileUploadComplete}
                  onUploadError={(error) => alert(error)}
                  compact={true}
                  aspectRatio={1}
                />
              )}
            </div>

            <div>
              <label className="block text-xs md:text-base font-bold text-gray-900 mb-1.5 md:mb-2">
                ชื่อ-นามสกุล / ชื่อแบรนด์ธุรกิจ *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="เช่น: สมชาย ใจดี"
                className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
              />
            </div>

            <div>
              <label className="block text-xs md:text-base font-bold text-gray-900 mb-1.5 md:mb-2">
                ประวัติผู้สอน (Bio)
              </label>
              <p className="text-[10px] md:text-sm text-gray-600 mb-2 md:mb-3">
                💡 สามารถจัดรูปแบบข้อความ เปลี่ยนสี ขนาด ใส่ลิงค์ รูปภาพ และวิดีโอได้
              </p>
              <RichTextEditor
                value={bio}
                onChange={setBio}
                placeholder="แนะนำตัวเองและประสบการณ์การสอน..."
                minHeight="250px"
              />
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl md:rounded-2xl p-3 md:p-6">
              <label className="block text-xs md:text-base font-bold text-gray-900 mb-2 md:mb-3 flex items-center gap-1.5 md:gap-2">
                <Globe className="w-4 md:w-5 h-4 md:h-5 text-blue-600" />
                รูปปก (Cover Image)
              </label>
              <p className="text-[10px] md:text-sm text-gray-600 mb-3 md:mb-4">
                💡 แนะนำ: รูปแนวนอน 1600×500px (อัตราส่วน 16:5)
              </p>
              {coverImage && coverImage.length > 0 ? (
                <div className="space-y-3">
                  <div className="relative bg-gray-100 rounded-xl">
                    <img
                      key={`cover-${coverImageKey}`}
                      src={coverImage}
                      alt="Cover Preview"
                      className="w-full aspect-[16/5] object-contain rounded-xl border-2 border-gray-200"
                    />
                  </div>
                  <button
                    onClick={() => {
                      setCoverImage("");
                      setCoverImageUploaded(false);
                      setCoverImageKey(Date.now());
                    }}
                    className="text-sm text-blue-600 hover:text-blue-700 font-semibold"
                  >
                    อัปโหลดรูปใหม่
                  </button>
                </div>
              ) : (
                <MediaUpload
                  key="cover-upload"
                  courseId={user?.uid || "temp"}
                  mediaType="image"
                  onUploadComplete={handleCoverUploadComplete}
                  onUploadError={(error) => alert(error)}
                  aspectRatio={16 / 5}
                />
              )}
            </div>
          </div>
        </div>
        )}

        {/* Appearance Tab */}
        {activeTab === "appearance" && (
        <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl p-4 md:p-6 lg:p-8 mb-4 md:mb-6 border border-blue-100">
          <div className="flex items-center gap-2 md:gap-4 mb-4 md:mb-8">
            <div className="w-10 md:w-12 h-10 md:h-12 bg-gradient-to-br from-pink-500 to-blue-500 rounded-lg md:rounded-xl flex items-center justify-center">
              <Palette className="w-5 md:w-6 h-5 md:h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg md:text-2xl font-bold text-gray-900">
                ธีมและรูปลักษณ์
              </h2>
              <p className="text-xs md:text-sm text-gray-500">ปรับแต่งสีและรูปแบบหน้าเว็บของคุณ</p>
            </div>
          </div>

          <div className="space-y-4 md:space-y-6">
            <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl md:rounded-2xl p-4 md:p-6">
              <label className="block text-sm md:text-base font-bold text-gray-900 mb-2 md:mb-3 flex items-center gap-1.5 md:gap-2">
                <Globe className="w-4 md:w-5 h-4 md:h-5 text-blue-600" />
                รูปภาพประกอบแนะนำตัว
              </label>
              <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4">
                💡 แนะนำ: รูปแนวตั้ง 800×1000px (อัตราส่วน 4:5)
              </p>
              {introImage ? (
                <div className="space-y-3">
                  <img
                    key={`intro-${introImageKey}`}
                    src={introImage}
                    alt="Intro Preview"
                    className="w-full max-w-xs mx-auto aspect-[4/5] object-cover rounded-xl border-2 border-gray-200"
                  />
                  <button
                    onClick={() => {
                      setIntroImage("");
                      setIntroImageUploaded(false);
                      setIntroImageKey(Date.now());
                    }}
                    className="text-sm text-blue-600 hover:text-blue-700 font-semibold"
                  >
                    อัปโหลดรูปใหม่
                  </button>
                </div>
              ) : (
                <MediaUpload
                  key="intro-upload"
                  courseId={user?.uid || "temp"}
                  mediaType="image"
                  onUploadComplete={handleIntroUploadComplete}
                  onUploadError={(error) => alert(error)}
                  aspectRatio={4 / 5}
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-textDark mb-2">
                สีธีมหลัก
              </label>
              <div className="flex items-center gap-4 mb-3">
                <input
                  type="color"
                  value={themeColor}
                  onChange={(e) => setThemeColor(e.target.value)}
                  className="w-20 h-12 rounded-xl border-2 border-gray-200 cursor-pointer"
                />
                <input
                  type="text"
                  value={themeColor}
                  onChange={(e) => setThemeColor(e.target.value)}
                  placeholder="#8B5CF6"
                  className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none transition-colors font-mono"
                />
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                {[
                  { color: '#8B5CF6', name: 'ม่วง' },
                  { color: '#3B82F6', name: 'น้ำเงิน' },
                  { color: '#06B6D4', name: 'ฟ้า' },
                  { color: '#10B981', name: 'เขียว' },
                  { color: '#F59E0B', name: 'ส้ม' },
                  { color: '#EF4444', name: 'แดง' },
                  { color: '#EC4899', name: 'ชมพู' },
                  { color: '#6366F1', name: 'คราม' },
                  { color: '#8B5A3C', name: 'น้ำตาล' },
                  { color: '#1F2937', name: 'เทาเข้ม' },
                ].map((preset) => (
                  <button
                    key={preset.color}
                    type="button"
                    onClick={() => setThemeColor(preset.color)}
                    className="group relative"
                    title={preset.name}
                  >
                    <div
                      className={`w-10 h-10 rounded-lg border-2 transition-all hover:scale-110 ${
                        themeColor.toLowerCase() === preset.color.toLowerCase()
                          ? 'border-gray-800 ring-2 ring-gray-400'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      style={{ backgroundColor: preset.color }}
                    />
                    <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      {preset.name}
                    </span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-textLight mt-2">
                สีนี้จะใช้เป็นสีหลักในหน้าผู้เรียนของคุณ
              </p>
            </div>

            {plan?.features?.hasDashboard && (
              <div>
                <label className="block text-sm font-semibold text-textDark mb-2">
                  สีหัวข้อ
                </label>
                <div className="flex items-center gap-4 mb-3">
                  <input
                    type="color"
                    value={headingColor}
                    onChange={(e) => setHeadingColor(e.target.value)}
                    className="w-20 h-12 rounded-xl border-2 border-gray-200 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={headingColor}
                    onChange={(e) => setHeadingColor(e.target.value)}
                    placeholder="#1F2937"
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none transition-colors font-mono"
                  />
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {[
                    { color: '#1F2937', name: 'เทาเข้ม' },
                    { color: '#374151', name: 'เทา' },
                    { color: '#111827', name: 'ดำ' },
                    { color: '#7C3AED', name: 'ม่วง' },
                    { color: '#2563EB', name: 'น้ำเงิน' },
                    { color: '#DC2626', name: 'แดง' },
                  ].map((preset) => (
                    <button
                      key={preset.color}
                      type="button"
                      onClick={() => setHeadingColor(preset.color)}
                      className="group relative"
                      title={preset.name}
                    >
                      <div
                        className={`w-10 h-10 rounded-lg border-2 transition-all hover:scale-110 ${
                          headingColor.toLowerCase() === preset.color.toLowerCase()
                            ? 'border-gray-800 ring-2 ring-gray-400'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                        style={{ backgroundColor: preset.color }}
                      />
                      <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {preset.name}
                      </span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-textLight mt-2">
                  สีสำหรับหัวข้อและข้อความสำคัญ
                </p>
              </div>
            )}

            {plan?.features?.hasDashboard && (
              <div>
                <label className="block text-sm font-semibold text-textDark mb-2">
                  สีปุ่มกดสำคัญ
                </label>
                <div className="flex items-center gap-4 mb-3">
                  <input
                    type="color"
                    value={buttonColor}
                    onChange={(e) => setButtonColor(e.target.value)}
                    className="w-20 h-12 rounded-xl border-2 border-gray-200 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={buttonColor}
                    onChange={(e) => setButtonColor(e.target.value)}
                    placeholder="#8B5CF6"
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none transition-colors font-mono"
                  />
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {[
                    { color: '#8B5CF6', name: 'ม่วง' },
                    { color: '#3B82F6', name: 'น้ำเงิน' },
                    { color: '#06B6D4', name: 'ฟ้า' },
                    { color: '#10B981', name: 'เขียว' },
                    { color: '#F59E0B', name: 'ส้ม' },
                    { color: '#EF4444', name: 'แดง' },
                    { color: '#EC4899', name: 'ชมพู' },
                    { color: '#6366F1', name: 'คราม' },
                  ].map((preset) => (
                    <button
                      key={preset.color}
                      type="button"
                      onClick={() => setButtonColor(preset.color)}
                      className="group relative"
                      title={preset.name}
                    >
                      <div
                        className={`w-10 h-10 rounded-lg border-2 transition-all hover:scale-110 ${
                          buttonColor.toLowerCase() === preset.color.toLowerCase()
                            ? 'border-gray-800 ring-2 ring-gray-400'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                        style={{ backgroundColor: preset.color }}
                      />
                      <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {preset.name}
                      </span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-textLight mt-2">
                  สีสำหรับปุ่มกดและ Call-to-Action สำคัญ
                </p>
              </div>
            )}
          </div>

          {/* Social Links */}
          {plan?.features?.hasDashboard && (
          <div className="mt-8 pt-8 border-t-2 border-gray-100">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                <LinkIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  ช่องทางติดต่อ
                </h2>
                <p className="text-sm text-gray-500">เชื่อมต่อ Social Media ของคุณ</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-textDark mb-2">
                  Facebook
                </label>
                <div className="flex items-center gap-2">
                  <span className="px-4 py-3 bg-gray-100 border-2 border-gray-200 rounded-xl text-gray-600 text-sm whitespace-nowrap">
                    facebook.com/
                  </span>
                  <input
                    type="text"
                    value={socialLinks.facebook}
                    onChange={(e) => setSocialLinks({...socialLinks, facebook: e.target.value})}
                    placeholder="yourpage"
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none transition-colors"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">ใส่เฉพาะชื่อเพจ เช่น: viitamint</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-textDark mb-2">
                  Line
                </label>
                <div className="flex items-center gap-2">
                  <span className="px-4 py-3 bg-gray-100 border-2 border-gray-200 rounded-xl text-gray-600 text-sm whitespace-nowrap">
                    @
                  </span>
                  <input
                    type="text"
                    value={socialLinks.line}
                    onChange={(e) => setSocialLinks({...socialLinks, line: e.target.value})}
                    placeholder="yourlineid"
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none transition-colors"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">ใส่เฉพาะ Line ID เช่น: viitamint</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-textDark mb-2">
                  Instagram
                </label>
                <div className="flex items-center gap-2">
                  <span className="px-4 py-3 bg-gray-100 border-2 border-gray-200 rounded-xl text-gray-600 text-sm whitespace-nowrap">
                    instagram.com/
                  </span>
                  <input
                    type="text"
                    value={socialLinks.instagram}
                    onChange={(e) => setSocialLinks({...socialLinks, instagram: e.target.value})}
                    placeholder="yourusername"
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none transition-colors"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">ใส่เฉพาะ username เช่น: viitamint</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-textDark mb-2">
                  YouTube
                </label>
                <div className="flex items-center gap-2">
                  <span className="px-4 py-3 bg-gray-100 border-2 border-gray-200 rounded-xl text-gray-600 text-sm whitespace-nowrap">
                    youtube.com/@
                  </span>
                  <input
                    type="text"
                    value={socialLinks.youtube}
                    onChange={(e) => setSocialLinks({...socialLinks, youtube: e.target.value})}
                    placeholder="yourchannel"
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none transition-colors"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">ใส่เฉพาะชื่อช่อง เช่น: viitamint</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-textDark mb-2">
                  Website
                </label>
                <div className="flex items-center gap-2">
                  <span className="px-4 py-3 bg-gray-100 border-2 border-gray-200 rounded-xl text-gray-600 text-sm whitespace-nowrap">
                    https://
                  </span>
                  <input
                    type="text"
                    value={socialLinks.website}
                    onChange={(e) => setSocialLinks({...socialLinks, website: e.target.value})}
                    placeholder="yourwebsite.com"
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none transition-colors"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">ใส่เฉพาะโดเมน เช่น: viitamint.com</p>
              </div>
            </div>
          </div>
          )}

          {/* Theme Layout Settings - Pro only */}
          {(tier === "pro" || tier === "pro_plus" || tier === "yearly") && (
            <div className="mt-8 pt-8 border-t-2 border-gray-100">
              <ThemeSelector
                selectedTheme={themeLayout}
                onThemeSelect={setThemeLayout}
                disabled={saving}
              />
            </div>
          )}
        </div>
        )}

        {/* URL Settings Tab */}
        {activeTab === "url" && (
          <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl p-4 md:p-6 lg:p-8 mb-4 md:mb-6 border border-blue-100">
            <div className="flex items-center gap-2 md:gap-4 mb-4 md:mb-8">
              <div className="w-10 md:w-12 h-10 md:h-12 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-lg md:rounded-xl flex items-center justify-center">
                <LinkIcon className="w-5 md:w-6 h-5 md:h-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg md:text-2xl font-bold text-gray-900">
                  ตั้งค่า URL
                </h2>
                <p className="text-xs md:text-sm text-gray-500">กำหนด URL สำหรับหน้าเว็บของคุณ</p>
              </div>
            </div>

            {/* Current URL Display with Copy Button - Always show for everyone */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl md:rounded-2xl p-4 md:p-6 mb-4 md:mb-6 border-2 border-green-200">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm md:text-base font-bold text-gray-900 mb-2">
                    {username ? 'URL ของคุณ' : 'URL ฟรี (แบบยาว)'}
                  </h3>
                  <div className="bg-white rounded-lg p-3 mb-3 border-2 border-green-300">
                    <p className="text-xs md:text-sm font-mono text-gray-700 break-all">
                      {username
                        ? (tier === "pro_plus_yearly" 
                          ? `https://${username}.edumi.co`
                          : hasUrlAddon && urlAddonType === 'subdomain'
                          ? `https://${username}.edumi.co`
                          : hasUrlAddon && urlAddonType === 'path'
                          ? `https://coursedee.web.app/${username}`
                          : `https://coursedee.web.app/instructor/${user?.uid}`)
                        : `https://coursedee.web.app/instructor/${user?.uid}`
                      }
                    </p>
                  </div>
                  {!username && tier !== "free" && (
                    <p className="text-xs text-gray-600 mb-3">
                      💡 ต้องการ URL สวยๆ เช่น <span className="font-mono text-blue-600">username.edumi.co</span>? ซื้อ Add-on URL ด้านล่าง
                    </p>
                  )}
                  <button
                    onClick={() => {
                      const url = username
                        ? (tier === "pro_plus_yearly" 
                          ? `https://${username}.edumi.co`
                          : hasUrlAddon && urlAddonType === 'subdomain'
                          ? `https://${username}.edumi.co`
                          : hasUrlAddon && urlAddonType === 'path'
                          ? `https://coursedee.web.app/${username}`
                          : `https://coursedee.web.app/instructor/${user?.uid}`)
                        : `https://coursedee.web.app/instructor/${user?.uid}`;
                      navigator.clipboard.writeText(url);
                      setMessage("คัดลอก URL สำเร็จ!");
                      setTimeout(() => setMessage(""), 3000);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all text-sm"
                  >
                    <Share2 className="w-4 h-4" />
                    คัดลอก URL
                  </button>
                </div>
              </div>
            </div>

            {tier === "free" ? (
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl md:rounded-2xl p-4 md:p-6 border-2 border-purple-200">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Gem className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm md:text-base font-bold text-gray-900 mb-2">ต้องการ URL สวยๆ?</h3>
                    <p className="text-xs md:text-sm text-gray-600 mb-3">
                      อัปเกรดเป็น <strong>Pro</strong> เพื่อซื้อ Add-on URL และใช้ URL ที่จดจำง่าย เช่น <span className="font-mono text-blue-600">username.edumi.co</span> หรือ <span className="font-mono text-blue-600">edumi.co/username</span>
                    </p>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                      <p className="text-xs text-yellow-800 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        <span>แพ็คเก็จ FREE ไม่สามารถซื้อ Add-on URL ได้</span>
                      </p>
                    </div>
                    <button
                      onClick={() => navigate('/subscription/renew')}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all text-sm"
                    >
                      <Gem className="w-4 h-4" />
                      ดูแพ็กเกจ Pro
                    </button>
                  </div>
                </div>
              </div>
            ) : tier === "pro_monthly" || tier === "pro_yearly" ? (
              <div>
                {existingUsername && originalUsername ? (
                  /* User has existing username - show current URL */
                  <div>
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <LinkIcon className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-blue-900 mb-2">
                            URL แบบ Path
                          </h3>
                          <p className="text-sm text-blue-800">
                            URL ของคุณ: <code className="bg-white px-2 py-1 rounded">edumi.co/{existingUsername}</code>
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-700 mb-2">
                        Username ของคุณ: <strong>{existingUsername}</strong>
                      </p>
                      <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded p-2">
                        <p className="text-xs text-yellow-800 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          หากต้องการเปลี่ยน Username กรุณาติดต่อ Admin
                        </p>
                      </div>
                    </div>
                  </div>
                ) : !hasUrlAddon ? (
                  /* No URL addon - Show purchase button */
                  <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <AlertCircle className="w-6 h-6 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-orange-900 mb-2">
                          ต้องการ URL สวยๆ?
                        </h3>
                        <p className="text-sm text-orange-800 mb-3">
                          ซื้อ URL Add-on เพื่อตั้งค่า Username และใช้งาน URL ที่จดจำง่าย
                        </p>
                        <div className="bg-white rounded-lg p-3 mb-3 border border-orange-200">
                          <p className="text-xs text-gray-700 mb-2">
                            <strong>ราคา URL Add-on สำหรับ Pro:</strong>
                          </p>
                          <ul className="text-xs text-gray-600 space-y-1 ml-4">
                            <li>• Path URL (edumi.co/username): <span className="font-semibold text-blue-600">฿49/เดือน</span></li>
                            <li>• Subdomain (username.edumi.co): <span className="font-semibold text-blue-600">฿99/เดือน</span></li>
                          </ul>
                        </div>
                        <button
                          onClick={() => navigate('/settings/url-addon')}
                          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
                        >
                          <Globe className="w-5 h-5" />
                          ซื้อ URL Add-on
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Has URL addon - allow setting username */
                  <div>
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <LinkIcon className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-blue-900 mb-2">
                            ตั้งค่า Username
                          </h3>
                          <p className="text-sm text-blue-800">
                            URL ของคุณจะเป็น: <code className="bg-white px-2 py-1 rounded">
                              {urlAddonType === 'subdomain' ? `${username || 'username'}.edumi.co` : `edumi.co/${username || 'username'}`}
                            </code>
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {urlAddonType === 'subdomain' ? (
                        <div className="flex items-center gap-2">
                          <div className="relative flex-1">
                            <input
                              type="text"
                              value={username}
                              onChange={(e) => setUsername(e.target.value.toLowerCase())}
                              placeholder="username"
                              className={`w-full px-4 py-3 pr-12 border-2 rounded-xl focus:outline-none transition-colors ${
                                !username || username === originalUsername
                                  ? 'border-gray-200 focus:border-primary'
                                  : usernameAvailable === true
                                  ? 'border-green-500 focus:border-green-500'
                                  : usernameAvailable === false
                                  ? 'border-red-500 focus:border-red-500'
                                  : 'border-gray-200 focus:border-primary'
                              }`}
                            />
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                              {isCheckingUsername ? (
                                <Loader className="w-5 h-5 text-gray-400 animate-spin" />
                              ) : usernameAvailable === true && username !== originalUsername ? (
                                <Check className="w-5 h-5 text-green-500" />
                              ) : usernameAvailable === false ? (
                                <X className="w-5 h-5 text-red-500" />
                              ) : null}
                            </div>
                          </div>
                          <span className="text-textLight">.edumi.co</span>
                        </div>
                      ) : (
                        <div className="relative">
                          <input
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value.toLowerCase())}
                            placeholder="username"
                            className={`w-full px-4 py-3 pr-12 border-2 rounded-xl focus:outline-none transition-colors ${
                              !username || username === originalUsername
                                ? 'border-gray-200 focus:border-primary'
                                : usernameAvailable === true
                                ? 'border-green-500 focus:border-green-500'
                                : usernameAvailable === false
                                ? 'border-red-500 focus:border-red-500'
                                : 'border-gray-200 focus:border-primary'
                            }`}
                          />
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            {isCheckingUsername ? (
                              <Loader className="w-5 h-5 text-gray-400 animate-spin" />
                            ) : usernameAvailable === true && username !== originalUsername ? (
                              <Check className="w-5 h-5 text-green-500" />
                            ) : usernameAvailable === false ? (
                              <X className="w-5 h-5 text-red-500" />
                            ) : null}
                          </div>
                        </div>
                      )}
                      {usernameErrorMessage && (
                        <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {usernameErrorMessage}
                        </p>
                      )}
                      {usernameAvailable === true && username !== originalUsername && (
                        <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          ใช้งานได้
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : tier === "pro_plus_monthly" ? (
              <div>
                {existingUsername && originalUsername ? (
                  /* User has existing username - show current URL */
                  <div>
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6 mb-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Globe className="w-6 h-6 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-purple-900 mb-2">
                            Subdomain
                          </h3>
                          <p className="text-sm text-purple-800">
                            URL ของคุณ: <code className="bg-white px-2 py-1 rounded">{existingUsername}.edumi.co</code>
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-700 mb-2">
                        Username ของคุณ: <strong>{existingUsername}</strong>
                      </p>
                      <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded p-2">
                        <p className="text-xs text-yellow-800 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          หากต้องการเปลี่ยน Username กรุณาติดต่อ Admin
                        </p>
                      </div>
                    </div>
                  </div>
                ) : !hasUrlAddon ? (
                  /* No URL addon - Show purchase button */
                  <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <AlertCircle className="w-6 h-6 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-orange-900 mb-2">
                          ต้องการ URL สวยๆ?
                        </h3>
                        <p className="text-sm text-orange-800 mb-3">
                          ซื้อ URL Add-on เพื่อตั้งค่า Username และใช้งาน Subdomain
                        </p>
                        <div className="bg-white rounded-lg p-3 mb-3 border border-orange-200">
                          <p className="text-xs text-gray-700 mb-2">
                            <strong>ราคา URL Add-on สำหรับ Pro+ Monthly:</strong>
                          </p>
                          <ul className="text-xs text-gray-600 space-y-1 ml-4">
                            <li>• Subdomain (username.edumi.co): <span className="font-semibold text-blue-600">฿99/เดือน</span></li>
                          </ul>
                        </div>
                        <button
                          onClick={() => navigate('/settings/url-addon')}
                          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
                        >
                          <Globe className="w-5 h-5" />
                          ซื้อ URL Add-on
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Has URL addon - allow setting username */
                  <div>
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6 mb-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Globe className="w-6 h-6 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-purple-900 mb-2">
                            ตั้งค่า Username
                          </h3>
                          <p className="text-sm text-purple-800">
                            URL ของคุณจะเป็น: <code className="bg-white px-2 py-1 rounded">{username || 'username'}.edumi.co</code>
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                          <input
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value.toLowerCase())}
                            placeholder="username"
                            className={`w-full px-4 py-3 pr-12 border-2 rounded-xl focus:outline-none transition-colors ${
                              !username || username === originalUsername
                                ? 'border-gray-200 focus:border-primary'
                                : usernameAvailable === true
                                ? 'border-green-500 focus:border-green-500'
                                : usernameAvailable === false
                                ? 'border-red-500 focus:border-red-500'
                                : 'border-gray-200 focus:border-primary'
                            }`}
                          />
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            {isCheckingUsername ? (
                              <Loader className="w-5 h-5 text-gray-400 animate-spin" />
                            ) : usernameAvailable === true && username !== originalUsername ? (
                              <Check className="w-5 h-5 text-green-500" />
                            ) : usernameAvailable === false ? (
                              <X className="w-5 h-5 text-red-500" />
                            ) : null}
                          </div>
                        </div>
                        <span className="text-textLight">.edumi.co</span>
                      </div>
                      {usernameErrorMessage && (
                        <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {usernameErrorMessage}
                        </p>
                      )}
                      {usernameAvailable === true && username !== originalUsername && (
                        <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          ใช้งานได้
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : tier === "pro_plus_yearly" ? (
              <div className="space-y-6">
                {/* Pro+ Yearly - Subdomain + Custom Domain */}
                <div className="bg-gradient-to-r from-pink-50 to-rose-50 border-2 border-pink-200 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Globe className="w-6 h-6 text-pink-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-pink-900 mb-2">
                        Pro+ Yearly - Subdomain + Custom Domain
                      </h3>
                      <div className="space-y-2 text-sm text-pink-800">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-pink-600" />
                          <span>ใช้ Subdomain: username.edumi.co</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-pink-600" />
                          <span>ใช้ Custom Domain: yourdomain.com</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* URL Type Selection */}
                <div>
                  <label className="block text-sm font-semibold text-textDark mb-3">
                    เลือกประเภท URL
                  </label>
                  <div className="flex gap-4">
                    <label className="flex-1 cursor-pointer">
                      <input
                        type="radio"
                        name="urlType"
                        checked={useSubdomain}
                        onChange={() => setUseSubdomain(true)}
                        className="sr-only"
                      />
                      <div className={`p-4 rounded-xl border-2 transition-all ${
                        useSubdomain 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}>
                        <div className="font-semibold text-textDark mb-1">Subdomain</div>
                        <div className="text-xs text-textLight">username.edumi.co</div>
                      </div>
                    </label>
                    <label className="flex-1 cursor-pointer">
                      <input
                        type="radio"
                        name="urlType"
                        checked={!useSubdomain}
                        onChange={() => setUseSubdomain(false)}
                        className="sr-only"
                      />
                      <div className={`p-4 rounded-xl border-2 transition-all ${
                        !useSubdomain 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}>
                        <div className="font-semibold text-textDark mb-1">Custom Domain</div>
                        <div className="text-xs text-textLight">yourdomain.com</div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Subdomain or Custom Domain Input */}
                {useSubdomain ? (
                  <div>
                    <label className="block text-sm font-semibold text-textDark mb-3">
                      ตั้งค่า Username
                    </label>

                    {existingUsername && originalUsername ? (
                      /* User has existing username - show info only (cannot change) */
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-700 mb-2">
                          Username ของคุณ: <strong>{existingUsername}</strong>
                        </p>
                        <p className="text-xs text-blue-600">
                          URL: {existingUsername}.edumi.co
                        </p>
                        <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded p-2">
                          <p className="text-xs text-yellow-800 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            หากต้องการเปลี่ยน Username กรุณาติดต่อ Admin
                          </p>
                        </div>
                      </div>
                    ) : (
                      /* No existing username - allow creating new one */
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="relative flex-1">
                            <input
                              type="text"
                              value={username}
                              onChange={(e) => setUsername(e.target.value.toLowerCase())}
                              placeholder="username"
                              className={`w-full px-4 py-3 pr-12 border-2 rounded-xl focus:outline-none transition-colors ${
                                !username || username === originalUsername
                                  ? 'border-gray-200 focus:border-primary'
                                  : usernameAvailable === true
                                  ? 'border-green-500 focus:border-green-500'
                                  : usernameAvailable === false
                                  ? 'border-red-500 focus:border-red-500'
                                  : 'border-gray-200 focus:border-primary'
                              }`}
                            />
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                              {isCheckingUsername ? (
                                <Loader className="w-5 h-5 text-gray-400 animate-spin" />
                              ) : usernameAvailable === true && username !== originalUsername ? (
                                <Check className="w-5 h-5 text-green-500" />
                              ) : usernameAvailable === false ? (
                                <X className="w-5 h-5 text-red-500" />
                              ) : null}
                            </div>
                          </div>
                          <span className="text-textLight">.edumi.co</span>
                        </div>
                        {usernameErrorMessage && (
                          <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {usernameErrorMessage}
                          </p>
                        )}
                        {usernameAvailable === true && username !== originalUsername && (
                          <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            ใช้งานได้
                          </p>
                        )}
                        <p className="text-xs text-textLight mt-2">
                          URL ของคุณจะเป็น: {username || "username"}.edumi.co
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Custom Domain Request Form */}
                    <div>
                      <label className="block text-sm font-semibold text-textDark mb-2">
                        Custom Domain ที่ต้องการใช้
                      </label>
                      <input
                        type="text"
                        value={customDomain}
                        onChange={(e) => setCustomDomain(e.target.value.toLowerCase())}
                        placeholder="courses.yourdomain.com"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none transition-colors"
                      />
                      <p className="text-xs text-textLight mt-2">
                        กรอก domain ที่คุณต้องการใช้ เช่น courses.yourdomain.com หรือ www.yourdomain.com
                      </p>
                    </div>

                    {/* Instructions */}
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border-2 border-blue-200">
                      <h4 className="font-semibold text-textDark mb-3 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-blue-600" />
                        ขั้นตอนการขอใช้ Custom Domain
                      </h4>
                      
                      <div className="space-y-2 text-sm text-blue-900">
                        <div className="flex items-start gap-2">
                          <span className="font-semibold min-w-[20px]">1.</span>
                          <span>กรอก Custom Domain ที่ต้องการด้านบน</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="font-semibold min-w-[20px]">2.</span>
                          <span>กดปุ่ม "ส่งคำขอ Custom Domain" ด้านล่าง</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="font-semibold min-w-[20px]">3.</span>
                          <span>Admin จะส่งคำแนะนำการตั้งค่า DNS ไปที่ Email ของคุณ</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="font-semibold min-w-[20px]">4.</span>
                          <span>ตั้งค่า CNAME Record ที่ผู้ให้บริการ domain ของคุณตามคำแนะนำ</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="font-semibold min-w-[20px]">5.</span>
                          <span>รอ 15-30 นาที เพื่อให้ DNS Propagate</span>
                        </div>
                      </div>
                    </div>

                    {/* Important Note */}
                    <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                      <p className="text-sm text-yellow-900 flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>
                          <strong>สำคัญ:</strong> คุณต้องตั้งค่า Subdomain ก่อน เพราะ Custom Domain จะชี้ไปที่ Subdomain ของคุณ (เช่น {username || 'username'}.edumi.co)
                        </span>
                      </p>
                    </div>

                    {/* Submit Button */}
                    <button
                      onClick={async () => {
                        if (!customDomain) {
                          alert('กรุณากรอก Custom Domain');
                          return;
                        }
                        if (!username) {
                          alert('กรุณาตั้งค่า Subdomain ก่อน');
                          return;
                        }
                        if (!user?.uid || !user?.email) {
                          alert('ไม่พบข้อมูลผู้ใช้');
                          return;
                        }

                        try {
                          setSaving(true);
                          await createCustomDomainRequest(
                            user.uid,
                            user.email,
                            username,
                            customDomain,
                            `${username}.edumi.co`
                          );
                          
                          setMessage('✅ ส่งคำขอสำเร็จ! Admin จะติดต่อกลับภายใน 24 ชั่วโมง');
                          setCustomDomain('');
                          setTimeout(() => setMessage(''), 5000);
                        } catch (error) {
                          console.error('Error submitting request:', error);
                          setError('เกิดข้อผิดพลาดในการส่งคำขอ กรุณาลองใหม่อีกครั้ง');
                          setTimeout(() => setError(''), 5000);
                        } finally {
                          setSaving(false);
                        }
                      }}
                      disabled={saving}
                      className="w-full bg-gradient-to-r from-blue-500 to-pink-500 text-white py-4 rounded-xl font-bold hover:shadow-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          กำลังส่งคำขอ...
                        </>
                      ) : (
                        <>
                          <Globe className="w-5 h-5" />
                          ส่งคำขอ Custom Domain
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}

        {/* Payment Settings Tab */}
        {activeTab === "payment" && (
        <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl p-4 md:p-6 lg:p-8 mb-4 md:mb-6 border-2 border-blue-100">
          <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
            <div className="w-10 md:w-12 h-10 md:h-12 bg-gradient-to-br from-blue-500 to-pink-500 rounded-lg md:rounded-xl flex items-center justify-center">
              <CreditCard className="w-5 md:w-6 h-5 md:h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg md:text-2xl font-bold text-gray-900">
                ข้อมูลการรับชำระเงิน
              </h2>
              <p className="text-xs md:text-sm text-textLight">ตั้งค่าช่องทางรับเงินจากนักเรียน</p>
            </div>
          </div>

          {/* Info Banner */}
          <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-3 md:p-4 mb-4 md:mb-6">
            <div className="flex items-start gap-2 md:gap-3">
              <CheckCircle className="w-4 md:w-5 h-4 md:h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs md:text-sm text-blue-900">
                <p className="font-semibold mb-1">สำคัญ!</p>
                <p>กรุณาตั้งค่า QR Code หรือเลขบัญชีอย่างน้อย 1 อย่าง เพื่อให้นักเรียนสามารถชำระเงินได้</p>
              </div>
            </div>
          </div>

          <div className="space-y-4 md:space-y-6">
            {/* QR Code Section */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl md:rounded-2xl p-4 md:p-6">
              <label className="block text-sm md:text-base font-bold text-textDark mb-2 md:mb-3 flex items-center gap-1.5 md:gap-2">
                <Smartphone className="w-4 md:w-5 h-4 md:h-5 text-blue-600" />
                QR Code PromptPay (แนะนำ)
              </label>
              <p className="text-xs md:text-sm text-textLight mb-3 md:mb-4">
                💡 อัปโหลด QR Code PromptPay เพื่อให้นักเรียนสแกนชำระเงินได้สะดวก
              </p>
              {paymentQRCode ? (
                <div className="bg-white rounded-xl p-6 border-2 border-blue-200">
                  <div className="flex flex-col md:flex-row items-center gap-6">
                    <img
                      src={paymentQRCode}
                      alt="QR Code Preview"
                      className="w-48 h-48 object-contain border-4 border-white shadow-lg rounded-xl"
                    />
                    <div className="flex-1 text-center md:text-left">
                      <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full mb-3">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-semibold">QR Code พร้อมใช้งาน</span>
                      </div>
                      <p className="text-sm text-textLight mb-4">
                        นักเรียนจะเห็น QR Code นี้เมื่อชำระเงินคอร์สของคุณ
                      </p>
                      <button
                        onClick={() => {
                          setPaymentQRCode("");
                          setPaymentQRUploaded(false);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700 font-semibold underline"
                      >
                        เปลี่ยน QR Code ใหม่
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl p-6 border-2 border-dashed border-blue-300">
                  <MediaUpload
                    courseId={user?.uid || "temp"}
                    mediaType="image"
                    onUploadComplete={(mediaId, url) => {
                      setPaymentQRCode(url);
                      setPaymentQRUploaded(true);
                    }}
                    onUploadError={(error) => alert(error)}
                  />
                </div>
              )}
            </div>

            {/* Bank Account Section */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl md:rounded-2xl p-4 md:p-6">
              <label className="block text-sm md:text-base font-bold text-textDark mb-2 md:mb-3 flex items-center gap-1.5 md:gap-2">
                <CreditCard className="w-4 md:w-5 h-4 md:h-5 text-blue-600" />
                เลขบัญชีธนาคาร (ตัวเลือก)
              </label>
              <p className="text-xs md:text-sm text-textLight mb-3 md:mb-4">
                💡 ระบุเลขบัญชีเป็นทางเลือกสำรอง กรณีที่นักเรียนไม่สามารถสแกน QR Code ได้
              </p>
              
              <div className="space-y-2 md:space-y-3">
                <div>
                  <label className="block text-xs md:text-sm font-medium text-textDark mb-1.5 md:mb-2">ธนาคาร</label>
                  <input
                    type="text"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    placeholder="เช่น: ธนาคารกสิกรไทย"
                    className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border-2 border-blue-200 rounded-lg md:rounded-xl focus:border-blue-500 focus:outline-none transition-colors bg-white"
                  />
                </div>
                
                <div>
                  <label className="block text-xs md:text-sm font-medium text-textDark mb-1.5 md:mb-2">ชื่อบัญชี</label>
                  <input
                    type="text"
                    value={accountName}
                    onChange={(e) => setAccountName(e.target.value)}
                    placeholder="เช่น: นายสมชาย ใจดี"
                    className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border-2 border-blue-200 rounded-lg md:rounded-xl focus:border-blue-500 focus:outline-none transition-colors bg-white"
                  />
                </div>
                
                <div>
                  <label className="block text-xs md:text-sm font-medium text-textDark mb-1.5 md:mb-2">เลขบัญชี</label>
                  <input
                    type="text"
                    value={bankAccount}
                    onChange={(e) => setBankAccount(e.target.value)}
                    placeholder="เช่น: 123-4-56789-0"
                    className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border-2 border-blue-200 rounded-lg md:rounded-xl focus:border-blue-500 focus:outline-none transition-colors bg-white"
                  />
                </div>
              </div>
              
              {bankAccount && bankName && accountName && (
                <div className="mt-3 flex items-center gap-2 text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">ข้อมูลบัญชีพร้อมใช้งาน</span>
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Messages */}
        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
            <p className="text-red-600 text-sm whitespace-pre-line">{error}</p>
          </div>
        )}

        {message && (
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 mb-6">
            <p className="text-green-700 text-sm whitespace-pre-line font-mono">{message}</p>
          </div>
        )}

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-gradient-to-r from-blue-600 via-pink-600 to-blue-600 text-white py-3 md:py-5 rounded-xl md:rounded-2xl font-bold text-base md:text-xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
        >
          {saving ? (
            <span className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              กำลังบันทึก...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <Save className="w-5 h-5" />
              บันทึกการตั้งค่า
            </span>
          )}
        </button>
      </div>
    </div>
  );
}

